import React, { useRef, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, ChevronRight, Code2, Sparkles } from "lucide-react";
import { parseAIMessage, extractFollowUpQuestions, maskStreamingContent, isDemoPlaceholder } from "../utils/formatting/parseAIMessage";
import { CourseCodeBlock } from "./CourseCodeBlock";
import { Loading } from "./common/Loading";
import type { TelegramUser } from "../types";

/** Обёртка таблицы: горизонтальная прокрутка + подсказка */
const TableScrollWrapper = ({ children }: { children: React.ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="my-2 max-w-full min-w-0">
      <div
        ref={scrollRef}
        className="w-full max-w-full overflow-x-auto overscroll-x-contain scroll-smooth min-w-0"
        style={{ maxWidth: "100%" }}
      >
        {children}
      </div>
      {hasOverflow && (
        <p className="mt-1 flex items-center justify-center gap-1 text-xs text-neutral-500">
          <ChevronLeft className="size-3 shrink-0" aria-hidden />
          <span>Прокрутите вправо</span>
          <ChevronRight className="size-3 shrink-0" aria-hidden />
        </p>
      )}
    </div>
  );
};

const markdownComponents = {
  p: ({ children, ...props }: React.ComponentPropsWithoutRef<"p">) => (
    <p className="mb-2 last:mb-0 text-sm break-words" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }} {...props}>{children}</p>
  ),
  strong: ({ children, ...props }: React.ComponentPropsWithoutRef<"strong">) => (
    <strong className="font-semibold" {...props}>{children}</strong>
  ),
  ul: ({ children, ...props }: React.ComponentPropsWithoutRef<"ul">) => (
    <ul className="list-disc ml-4 mb-2 text-sm space-y-1 break-words" style={{ wordBreak: "break-word" }} {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.ComponentPropsWithoutRef<"ol">) => (
    <ol className="list-decimal ml-4 mb-2 text-sm space-y-1 break-words" style={{ wordBreak: "break-word" }} {...props}>{children}</ol>
  ),
  li: ({ children, ordered, ...props }: React.ComponentPropsWithoutRef<"li"> & { ordered?: boolean }) => (
    <li className="break-words pl-0.5" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }} {...props}>{children}</li>
  ),
  a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<"a">) => (
    <a href={href} className="text-primary hover:underline" {...props}>{children}</a>
  ),
  code: ({ inline, children, ...props }: React.ComponentPropsWithoutRef<"code"> & { inline?: boolean }) =>
    inline ? (
      <code className="bg-neutral-light rounded px-1.5 py-0.5 text-xs font-mono break-words" style={{ wordBreak: "break-word" }} {...props}>{children}</code>
    ) : (
      <code className="block max-w-full bg-neutral-light rounded p-2 text-xs font-mono whitespace-pre-wrap break-words" {...props}>{children}</code>
    ),
  pre: ({ children, ...props }: React.ComponentPropsWithoutRef<"pre">) => (
    <pre className="my-2 max-w-full overflow-hidden rounded-lg bg-neutral-light/60 p-2 text-xs font-mono whitespace-pre-wrap break-words" {...props}>{children}</pre>
  ),
  table: ({ children, ...props }: React.ComponentPropsWithoutRef<"table">) => (
    <TableScrollWrapper>
      <table className="w-max min-w-max border-collapse text-sm" {...props}>{children}</table>
    </TableScrollWrapper>
  ),
  thead: (props: React.ComponentPropsWithoutRef<"thead">) => <thead className="border-b-2 border-neutral-border" {...props} />,
  tbody: (props: React.ComponentPropsWithoutRef<"tbody">) => <tbody {...props} />,
  tr: (props: React.ComponentPropsWithoutRef<"tr">) => <tr className="border-b border-neutral-border/60" {...props} />,
  th: (props: React.ComponentPropsWithoutRef<"th">) => (
    <th className="text-left font-semibold py-1.5 px-2 min-w-[6rem] align-top bg-neutral-light/50 text-sm whitespace-nowrap" {...props} />
  ),
  td: (props: React.ComponentPropsWithoutRef<"td">) => (
    <td className="py-1.5 px-2 min-w-[6rem] align-top text-sm break-words" style={{ wordBreak: "break-word" }} {...props} />
  ),
};

/** Самосворачиваемый блок SQL-кода — аналог QuizCard */
const CollapsibleSqlBlock = ({
  isPlaceholder,
  label,
  sqlCode,
  sqlType,
  user,
}: {
  isPlaceholder: boolean;
  label: string;
  sqlCode: string;
  sqlType?: "runnable" | "illustrative";
  user: TelegramUser | null;
}) => {
  const [open, setOpen] = useState(false);
  const firstLine = isPlaceholder ? null : (sqlCode || "").split("\n").find((l) => l.trim()) ?? "";

  return (
    <div className="my-2 rounded-xl border border-neutral-200 bg-neutral-50 shadow-sm">
      <button
        type="button"
        disabled={isPlaceholder}
        onClick={() => !isPlaceholder && setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors rounded-xl ${
          isPlaceholder ? "cursor-default" : "hover:bg-neutral-100/80 cursor-pointer"
        }`}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-neutral-200 text-neutral-500 shrink-0">
          <Code2 className="w-3.5 h-3.5" />
        </span>
        {isPlaceholder ? (
          <span className="flex items-center gap-1 text-xs text-neutral-400 flex-1">
            <span className="w-1 h-1 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1 h-1 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1 h-1 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
            <span className="ml-1">{label}</span>
          </span>
        ) : (
          <span className="font-mono text-xs text-neutral-500 truncate flex-1 min-w-0">{firstLine}</span>
        )}
        <span className="shrink-0 text-[10px] rounded px-1.5 py-0.5 bg-neutral-200 text-neutral-400">
          {sqlType === "illustrative" ? "пример" : "SQL"}
        </span>
        {!isPlaceholder && (
          <ChevronRight className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-150 shrink-0 ${open ? "rotate-90" : ""}`} />
        )}
      </button>

      {open && !isPlaceholder && (
        <div className="border-t border-neutral-200 min-w-0 max-w-full overflow-hidden rounded-xl bg-white">
          <CourseCodeBlock
            initialSql={sqlCode}
            user={user}
            isDemo={true}
            compact={true}
            illustrative={sqlType === "illustrative"}
          />
        </div>
      )}
    </div>
  );
};

/** Самосворачиваемый блок «Возможные вопросы» */
const FollowUpBlock = ({
  questions,
  onQuestionClick,
  followUpButtonClass,
}: {
  questions: string[];
  onQuestionClick: (q: string) => void;
  followUpButtonClass: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 rounded-xl border border-primary/20 bg-primary/5 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-primary/10 transition-colors rounded-xl cursor-pointer"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary shrink-0">
          <Sparkles className="w-3.5 h-3.5" />
        </span>
        <span className="text-sm font-semibold text-primary/90 flex-1">Что спросить дальше</span>
        <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-primary/15 text-primary shrink-0">
          {questions.length}
        </span>
        <ChevronRight className={`w-3.5 h-3.5 text-primary/50 transition-transform duration-150 shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-primary/10 pt-2.5 flex flex-wrap gap-2">
          {questions.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onQuestionClick(q)}
              className={`text-xs px-2.5 py-2 min-h-[44px] rounded-lg text-neutral-text transition-colors leading-relaxed text-left focus:outline-none focus:ring-2 focus:ring-primary/30 ${followUpButtonClass}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Placeholder для блока кода во время генерации/валидации. Экспортируется для плейсхолдера подборки в ChatCore. */
export const CodeBlockPlaceholder = ({ label }: { label: string }) => (
  <div
    className="my-2 rounded-lg bg-neutral-light/80 border border-neutral-border/60 p-4 flex items-center gap-2 text-sm text-neutral-text/80"
    role="status"
    aria-live="polite"
  >
    <span className="inline-flex gap-1 shrink-0" aria-hidden>
      <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
    <span>{label}</span>
  </div>
);

export interface AITutorMessageContentProps {
  content: string;
  user: TelegramUser | null;
  /** Если true, извлекает follow-up вопросы (используется в панелях) */
  extractFollowUps?: boolean;
  /** Показывать кнопки «Вопросы дальше» (когда последнее сообщение, не loading) */
  showFollowUpButtons?: boolean;
  /** Callback при клике на вопрос */
  onQuestionClick?: (question: string) => void;
  /** Дополнительные классы для кнопок (border, etc.) */
  followUpButtonClass?: string;
  /** Показывать placeholder вместо [demo:...] (пока идёт генерация/валидация) */
  codeBlocksPlaceholder?: boolean;
  /** Этап загрузки (validating → «Проверяем код», иначе «Генерируем код») */
  loadingStage?: string | null;
  /** Показывать в конце сообщения плейсхолдер «Формируем подборку…» (контекст tasklist, идёт стрим) */
  tasklistPlaceholder?: boolean;
  /** SQL в ответе был проверен на выполнимость — показать подпись «Код проверен» */
  sqlValidated?: boolean;
}

/**
 * Рендерит сообщение AI с поддержкой [demo:...], таблиц, overflow, «Вопросы дальше».
 * Используется во всех панелях AI Tutor.
 */
export const AITutorMessageContent = ({
  content,
  user,
  extractFollowUps = true,
  showFollowUpButtons = false,
  onQuestionClick,
  followUpButtonClass = "border border-neutral-border bg-white hover:border-primary/40 hover:bg-primary/5",
  codeBlocksPlaceholder = false,
  loadingStage = null,
  tasklistPlaceholder = false,
  sqlValidated = false,
}: AITutorMessageContentProps) => {
  const needFollowUpParse = extractFollowUps || showFollowUpButtons;
  const { contentWithoutBlock, questions: followUpQuestions } = needFollowUpParse
    ? extractFollowUpQuestions(content)
    : { contentWithoutBlock: content, questions: [] as string[] };
  const textContent = extractFollowUps ? contentWithoutBlock : content;
  const contentToParse = codeBlocksPlaceholder
    ? maskStreamingContent(textContent)
    : textContent;
  const parts = parseAIMessage(contentToParse);

  return (
    <div
      className="space-y-2 break-words min-w-0 max-w-full overflow-hidden font-sans text-sm leading-relaxed"
      style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
    >
      {parts.map((part, index) => {
        if (part.type === "text") {
          return (
            <div key={index} className="min-w-0 max-w-full overflow-hidden">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {part.content || ""}
              </ReactMarkdown>
            </div>
          );
        }
        if (part.type === "code" && part.sqlCode) {
          const isPlaceholder = codeBlocksPlaceholder || isDemoPlaceholder(part.sqlCode);
          const label =
            loadingStage === "validating"
              ? "Проверяем код…"
              : loadingStage === "generating_sql"
                ? "Готовим практические материалы"
                : "Генерируем код…";
          return (
            <CollapsibleSqlBlock
              key={`${index}-${isPlaceholder ? "ph" : "code"}`}
              isPlaceholder={isPlaceholder}
              label={label}
              sqlCode={part.sqlCode}
              sqlType={part.sqlType}
              user={user}
            />
          );
        }
        return null;
      })}
      {tasklistPlaceholder && <CodeBlockPlaceholder label="Формируем подборку…" />}
      {sqlValidated && parts.some((p) => p.type === "code" && p.sqlCode && !isDemoPlaceholder(p.sqlCode)) && (
        <p className="mt-1.5 text-xs text-neutral-500 flex items-center gap-1" role="status">
          <span className="inline-block w-1 h-1 rounded-full bg-green-500" aria-hidden />
          Код проверен
        </p>
      )}
      {showFollowUpButtons && followUpQuestions.length > 0 && onQuestionClick && (
        <FollowUpBlock questions={followUpQuestions} onQuestionClick={onQuestionClick} followUpButtonClass={followUpButtonClass} />
      )}
    </div>
  );
};

/** Краткие подписи этапов для typing indicator */
export const STAGE_LABELS: Record<string, string> = {
  analysis: "Ищу в материалах…",
  research: "Ищу в материалах…",
  generation: "Готовлю ответ…",
  generating_sql: "Готовим практические материалы",
  validating: "Проверяем код…",
};

export function getStageLabel(stage: string | null): string {
  return (stage && STAGE_LABELS[stage]) || "Обрабатываю…";
}

/** Индикатор ожидания ответа: та же анимация, что на странице запроса (Loader12) + этап */
export const AITutorTypingIndicator = ({ stage }: { stage: string | null }) => (
  <Loading variant="inline" message={getStageLabel(stage)} />
);
